/**
 * useSessionUrl 훅
 * URL에서 세션 키 파싱 및 URL 업데이트 관리
 */

import { useState, useEffect, useCallback } from 'react';

interface UseSessionUrlResult {
  /** URL에서 파싱된 세션 키 (없으면 null) */
  sessionKeyFromUrl: string | null;
  /** 세션 키로 URL 업데이트 */
  updateUrlWithSession: (sessionKey: string) => void;
  /** URL에서 세션 키 제거 */
  clearSessionFromUrl: () => void;
  /** URL 기반 세션 처리 완료 표시 */
  markUrlSessionHandled: () => void;
}

/**
 * URL 경로에서 세션 키를 파싱하고 관리하는 훅
 * URL 형식: /[SESSIONKEY] (예: /ABC12345)
 */
export function useSessionUrl(): UseSessionUrlResult {
  const [sessionKeyFromUrl, setSessionKeyFromUrl] = useState<string | null>(() => {
    // 초기 로드 시 URL에서 세션 키 파싱
    const path = window.location.pathname;
    const sessionKey = parseSessionKeyFromPath(path);
    return sessionKey;
  });

  // URL 변경 감지 (브라우저 뒤로/앞으로 버튼)
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const sessionKey = parseSessionKeyFromPath(path);
      setSessionKeyFromUrl(sessionKey);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // 세션 키로 URL 업데이트
  const updateUrlWithSession = useCallback((sessionKey: string) => {
    const newPath = `/${sessionKey}`;
    if (window.location.pathname !== newPath) {
      window.history.pushState({ sessionKey }, '', newPath);
    }
  }, []);

  // URL에서 세션 키 제거
  const clearSessionFromUrl = useCallback(() => {
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }
    setSessionKeyFromUrl(null);
  }, []);

  // URL 기반 세션 처리 완료 표시 (다이얼로그가 열린 후)
  const markUrlSessionHandled = useCallback(() => {
    setSessionKeyFromUrl(null);
  }, []);

  return {
    sessionKeyFromUrl,
    updateUrlWithSession,
    clearSessionFromUrl,
    markUrlSessionHandled,
  };
}

/**
 * URL 경로에서 세션 키 파싱
 * 유효한 세션 키: 알파벳/숫자로 이루어진 4자리 이상 문자열
 */
function parseSessionKeyFromPath(path: string): string | null {
  // 경로에서 앞쪽 슬래시 제거
  const trimmedPath = path.replace(/^\/+/, '').replace(/\/+$/, '');

  // 빈 경로면 null
  if (!trimmedPath) {
    return null;
  }

  // 세션 키 유효성 검사 (알파벳/숫자, 4자리 이상)
  const sessionKeyPattern = /^[A-Za-z0-9]{4,}$/;
  if (sessionKeyPattern.test(trimmedPath)) {
    return trimmedPath.toUpperCase();
  }

  return null;
}

export default useSessionUrl;
